import DocumentWishTag, { type Document } from 'components/UI/DocumentWishTag'
import Modal from 'components/UI/Modal'
import { API_URL_TRAINING_PLAN_HISTORY } from 'constants/api'
import { PROFILE_WISH_DETAILS } from 'constants/profile'
import { useI18n } from 'context/I18nProvider'
import { useProfile } from 'context/ProfileProvider'
import useSWR from 'hooks/swr'
import './styles.scss'
import { Icon } from '@eleo-rh/core-ui'
import { Timeline } from 'antd'
import Descriptions from 'components/UI/Descriptions'
import Divider from 'components/UI/Divider'
import SectionTitle from 'components/UI/SectionTitle'
import Spin from 'components/UI/Spin'
import { API_TRAINING_WISHES } from 'constants/api-v2'
import dayjs from 'dayjs'
import { useModules } from 'hooks/modules'
import { Fragment, type ReactNode } from 'react'
import type { Plan, TrainingWish } from 'shared/types'
import { translateEvents } from './Timeline/translator'

interface HistoryEvent {
  creatorFullName: string
  createdAt: string
  updatedAt: string
  event: string
  context: {
    sessionCode: string
  }
}

interface StatusTransition {
  id: number
  oldStatus: string | null
  newStatus: string
  createdAt: string
  safetyImport: boolean
}

interface DetailsProps {
  id: number
  onClose: () => void
}

type DescriptionItem = { key: string; label?: string; children: ReactNode }

type TimelineEvent = {
  id: number
  date: string
  topic: string
  mention?: string
  validationDate?: string
}

const REQUESTED_STATUSES: string[] = ['initied', 'allowed']

const REQUESTED_STATUS_KEY = 'training-wish-status-transition.requested.label'

const ACCOMPLISHED_STATUS_KEY = 'training-wish-status-transition.accomplished.label'

const UNMANAGED_STATUS_KEYS: Record<string, string> = {
  accomplished: ACCOMPLISHED_STATUS_KEY,
  lost: ACCOMPLISHED_STATUS_KEY,
  unaccomplished: 'training-wish-status-transition.unaccomplished.label',
}

const VALIDATION_DATE_STATUSES: string[] = ['accomplished', 'lost']

const IMPORT_MENTION_KEY = 'training-wish-status-transition.import.label'

const CREATION_MENTION_KEY = 'training-wish-status-transition.creation.label'

const CREATION_MENTION_ORIGIN = 'accreditation_catalog'

const LOCALE_DATE_FORMATS = ['DD/MM/YYYY', 'YYYY-MM-DD']

const formatLocaleDate = (value?: string) => {
  const date = value ? dayjs(value, LOCALE_DATE_FORMATS) : null
  return date?.isValid() ? date.format('DD/MM/YYYY') : null
}

const formatDate = (value?: string) => {
  const date = value ? dayjs(value) : null
  return date?.isValid() ? date.format('DD/MM/YYYY') : null
}

const UNMANAGED_KEYS: string[] = [
  'label.trainee',
  'label.vintage',
  'label.date',
  'label.request_expressed_by',
  'label.category',
  'label.theme',
  'name',
  'label.status',
  'label.training_wish.origin',
  'label.training_wish.id',
]

function Details({ id, onClose }: DetailsProps) {
  const { i18n, i18name } = useI18n()
  const { profile, hasPermission } = useProfile()
  const { isSafetyStandalone } = useModules()

  const { data: wish, isValidating } = useSWR<TrainingWish>(
    `${API_TRAINING_WISHES}/${id}?groups[]=api:needs:view&groups[]=training-wishes:unmanaged`,
  )

  const provision = wish?.manager && !wish?.user
  const trainingName = wish && `${i18name(wish.training)} (${wish.training.code})`
  const admin = profile.trainingWishDetails === PROFILE_WISH_DETAILS.ADMIN

  const isUnmanagedTraining = isSafetyStandalone || wish?.unmanaged

  const { data: history, isValidating: isHistoryLoading } = useSWR<HistoryEvent[]>(
    isUnmanagedTraining
      ? `/api/2.0/training-wish/${id}/status-transitions`
      : API_URL_TRAINING_PLAN_HISTORY.replace('ID', String(id)),
  )

  const translateStatus = (status: string) => {
    if (!isUnmanagedTraining) return i18n(status)
    if (REQUESTED_STATUSES.includes(status)) return i18n(REQUESTED_STATUS_KEY)
    return i18n(UNMANAGED_STATUS_KEYS[status] ?? status)
  }

  const resolveMention = (transition: StatusTransition) => {
    if (transition.safetyImport) return IMPORT_MENTION_KEY
    if (!transition.oldStatus && wish?.origin === CREATION_MENTION_ORIGIN) return CREATION_MENTION_KEY
    return undefined
  }

  const buildUnmanagedEvent = (transition: StatusTransition): TimelineEvent => ({
    id: transition.id,
    date: formatDate(transition.createdAt) ?? '-',
    topic: translateStatus(transition.newStatus),
    mention: resolveMention(transition),
    validationDate: VALIDATION_DATE_STATUSES.includes(transition.newStatus)
      ? (formatDate(wish?.validationDate) ?? undefined)
      : undefined,
  })

  const events: TimelineEvent[] =
    !isHistoryLoading && history
      ? isUnmanagedTraining
        ? (history as unknown as StatusTransition[]).map(buildUnmanagedEvent)
        : translateEvents(
            history.filter(({ event }) => !['mail', 'mail_collaborator', 'mail_manager'].includes(event)),
            i18n,
          )
      : []

  const mapDispositives = (arr: Plan[]) =>
    arr
      .map(item => i18name(item.plan))
      .reduce((unique, item) => {
        if (!unique.includes(item)) unique.push(item)
        return unique
      }, [])
      .join(', ')

  const type = wish?.trainingWishPathwayId
    ? i18n('label.request_pathway')
    : wish?.training?.fictive
      ? i18n('label.outside_catalog')
      : i18n('label.request_catalog')

  const waiting = isValidating || !wish

  const canViewTimeline = hasPermission('TRAINING_WISH_CAN_VIEW_TIMELINE')
  const hasDocuments = (wish?.documents?.length ?? 0) > 0

  const descriptionSections: DescriptionItem[][] = wish
    ? [
        [
          ...(provision
            ? [
                {
                  key: 'modal.detail.managerprovision',
                  children: wish.manager?.fullName,
                },
              ]
            : [{ key: 'label.trainee', children: wish.user?.fullName }]),
          {
            key: 'label.quantity',
            children: wish.currentProvisions === null ? 1 : wish.currentProvisions,
          },
        ],
        [
          { key: 'label.type', children: type },

          {
            key: 'label.training_wish.origin',
            children: wish.origin ? i18n(`trainingWish.origin.${wish.origin}`) : '-',
          },

          { key: 'label.vintage', children: wish.vintage ? i18name(wish.vintage) : '-' },

          {
            key: 'label.training_wish.id',
            children: wish.unmanaged ? (wish.externalId ?? id) : id || '-',
          },

          { key: 'label.attribut', children: i18n(wish.wishPeriodType) },
          ...(hasPermission('TRAINING_WISH_CAN_VIEW_PRIORITY')
            ? [{ key: 'label.priority', children: i18name(wish.priority) }]
            : []),
          {
            key: 'label.date',
            label: isUnmanagedTraining ? 'label.training_wish.created_in_eleo_at' : undefined,
            children: formatLocaleDate(wish.creationDate) ?? '-',
          },
          { key: 'label.request_expressed_by', children: wish.creator?.fullName },
        ],
        [
          { key: 'label.category', children: i18name(wish.training.category) },
          { key: 'label.theme', children: i18name(wish.training.theme) },
          { key: 'name', children: trainingName },
          { key: 'label.description', children: trainingName },
          ...(wish?.trainingWishPathwayId
            ? [
                {
                  key: 'label.linked.pathway',
                  children: wish?.trainingPathway ? i18name(wish?.trainingPathway) : '-',
                },
                {
                  key: 'label.requested.linked.pathway',
                  children: wish?.trainingPathway?.id,
                },
              ]
            : []),
          { key: 'label.duration', children: `${wish.duration} ${i18n('label.hours')}` },
          { key: 'label.cost', children: `${wish.cost} €` },
        ],
        [
          { key: 'label.objective', children: wish.origin === 'accreditation_catalog' ? '-' : wish.why },
          { key: 'label.comment', children: wish.comment },
        ],
        [
          {
            key: 'label.status',
            label: isUnmanagedTraining ? 'label.training_wish.current_status' : undefined,
            children: translateStatus(wish.status),
          },
          {
            key: 'label.absence_reason',
            children: wish.absenceReason && i18name(wish.absenceReason),
          },
          {
            key: 'label.delete_reason',
            children: wish.deleteReason && i18name(wish.deleteReason),
          },
          { key: 'label.delete_reason_comment', children: wish.deleteReasonComment },
          { key: 'trainingWishCancelReason', children: i18name(wish.motifCancel) },
          {
            key: 'trainingWishUnCancelComment',
            children: wish.commentUncancel || '-',
          },
          { key: 'label.refusal', children: i18name(wish.motifRefusal) },
          { key: 'label.valdate_to_the_plan', children: wish.validateLabel ? i18n(wish.validateLabel) : '-' },
        ],
        ...(admin
          ? [
              [
                { key: 'label.project', children: i18name(wish.project) || '-' },
                { key: 'label.motif', children: i18name(wish.reason) || '-' },
                {
                  key: 'label.trainingWishOrientation',
                  children: i18name(wish.trainingWishOrientation) || '-',
                },
                { key: 'label.perimeter', children: i18name(wish.trainingWishPerimeter) || '-' },
                { key: 'label.budget', children: i18name(wish.budget) || '-' },
                {
                  key: 'label.plan',
                  children: wish.trainingWishPlans ? mapDispositives(wish.trainingWishPlans) : 'n/a',
                },
              ],
            ]
          : []),
      ]
    : []

  const contextKeys = isUnmanagedTraining ? UNMANAGED_KEYS : null

  const displayedDescriptionSections = contextKeys
    ? descriptionSections
        .map(section => section.filter(item => contextKeys.includes(item.key)))
        .filter(section => section.length > 0)
    : descriptionSections

  return (
    <Modal
      title={
        <SectionTitle size="xl">
          {i18n('label.training_wish_details_view')} N°{wish?.unmanaged ? (wish?.externalId ?? wish?.id) : wish?.id}
        </SectionTitle>
      }
      onCancel={onClose}
      width="80%"
      footer={null}
      closable={{
        closeIcon: <Icon name="xmark" />,
      }}
    >
      {waiting ? (
        <Spin />
      ) : (
        <div className="grid grid-cols-[1fr_496px] gap-8 mt-4">
          <div className="flex flex-col gap-4">
            {displayedDescriptionSections.map((section, i) => (
              <Fragment key={JSON.stringify(section)}>
                <Descriptions items={section} />
                {i === displayedDescriptionSections.length - 1 ? null : <Divider />}
              </Fragment>
            ))}
          </div>
          {(canViewTimeline || hasDocuments) && (
            <div className="bg-slate-50 rounded-xxs flex flex-col py-4 px-6">
              {canViewTimeline && (
                <Timeline
                  variant="filled"
                  items={events.map(event => ({
                    content: (
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-bold">{event.date}</span>
                        {event.mention && <span>_ {i18n(event.mention)} _</span>}
                        {event.topic}
                        {event.validationDate && (
                          <span className="text-slate-500">
                            ({i18n('training-wish-status-transition.validation_date.label')} : {event.validationDate})
                          </span>
                        )}
                      </div>
                    ),
                  }))}
                />
              )}
              {canViewTimeline && hasDocuments && <Divider />}
              {hasDocuments && (
                <>
                  <SectionTitle level={3} size="md" className="mt-4 text-slate-500">
                    {i18n('documents.title-join')}
                  </SectionTitle>
                  {wish.documents?.map((el: Document) => (
                    <DocumentWishTag key={el['@id'] || el.fileName} {...el} />
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

export default Details
